import once from 'lodash/once';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { getAssetMetadata } from './player/asset';

const loadTrackingPixel = (pixelUrl?: string) => {
  if (pixelUrl) {
    const pixel = new Image();
    pixel.src = pixelUrl;
  }
};

export type PixelTrackers = {
  track0: () => void;
  track25: () => void;
  track50: () => void;
  track75: () => void;
  track100: () => void;
};

/**
 * Reads the `pixeltracker*` URLs from the asset metadata and returns a set of
 * single-fire trackers (each one only triggers a pixel request the first time
 * it is invoked).
 */
export const getPixelTrackers = (asset: Asset): PixelTrackers => {
  const {
    pixeltrackerstart,
    pixeltracker25,
    pixeltracker50,
    pixeltracker75,
    pixeltrackerend,
  } = getAssetMetadata(asset);

  return {
    track0: once(() => loadTrackingPixel(pixeltrackerstart)),
    track25: once(() => loadTrackingPixel(pixeltracker25)),
    track50: once(() => loadTrackingPixel(pixeltracker50)),
    track75: once(() => loadTrackingPixel(pixeltracker75)),
    track100: once(() => loadTrackingPixel(pixeltrackerend)),
  };
};

/**
 * Fires the `track0`/`track25`/`track50`/`track75` pixels based on the
 * current playback progress. Trackers are single-fire, so it is safe to call
 * this on every `time` event.
 */
export const trackPixelProgress = (
  trackers: PixelTrackers,
  currentTimeInSeconds: number,
  totalTimeInSeconds: number
) => {
  trackers.track0();

  if (currentTimeInSeconds / totalTimeInSeconds > 0.25) {
    trackers.track25();
  }
  if (currentTimeInSeconds / totalTimeInSeconds > 0.5) {
    trackers.track50();
  }
  if (currentTimeInSeconds / totalTimeInSeconds > 0.75) {
    trackers.track75();
  }
};
